// audio.js — WebAudio によるシンセ音源。音声ファイルを一切持たないので
// オフラインでも即鳴る。効果音はすべてその場で合成する。

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

export class AudioEngine {
  constructor() {
    this.ctx = null;
    this.ready = false;
    this.muted = false;
    this._musicMode = 'idle';
    this._step = 0;
    this._nextNoteTime = 0;
    this._timer = null;
    this._lastPeg = 0;
    this._lastClack = 0;
  }

  // 音は必ずユーザー操作の中で初期化する（自動再生ポリシー対策）
  init() {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.9;

    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = -14;
    this.comp.knee.value = 24;
    this.comp.ratio.value = 10;
    this.comp.attack.value = 0.003;
    this.comp.release.value = 0.18;

    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = 1.0;
    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = 0.45;

    // 簡易リバーブ（インパルス応答をノイズから生成）
    this.reverb = ctx.createConvolver();
    this.reverb.buffer = this._impulse(1.6, 2.6);
    this.revSend = ctx.createGain();
    this.revSend.gain.value = 0.22;

    this.sfxBus.connect(this.comp);
    this.musicBus.connect(this.comp);
    this.sfxBus.connect(this.revSend);
    this.revSend.connect(this.reverb);
    this.reverb.connect(this.comp);
    this.comp.connect(this.master);
    this.master.connect(ctx.destination);

    this.noiseBuf = this._noise(1.0);
    this.ready = true;
    this._nextNoteTime = ctx.currentTime;
    this._startScheduler();
  }

  _impulse(dur, decay) {
    const ctx = this.ctx, len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
      }
    }
    return buf;
  }

  _noise(dur) {
    const ctx = this.ctx, len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  setMuted(m) {
    this.muted = m;
    if (this.master) {
      this.master.gain.setTargetAtTime(m ? 0 : 0.9, this.ctx.currentTime, 0.02);
    }
  }

  get t() { return this.ctx.currentTime; }

  // --- 基本音色 ------------------------------------------------------

  _env(gain, t, a, d, peak) {
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + a);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + a + d);
  }

  tone({ freq, type = 'sine', dur = 0.2, attack = 0.004, gain = 0.3, t, detune = 0, dest, sweepTo = 0, bend = 0 }) {
    if (!this.ready) return;
    t = t ?? this.t;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (sweepTo) o.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), t + dur);
    if (bend) o.detune.setValueAtTime(detune + bend, t);
    else o.detune.value = detune;
    this._env(g, t, attack, dur, gain);
    o.connect(g).connect(dest || this.sfxBus);
    o.start(t);
    o.stop(t + dur + attack + 0.05);
  }

  noise({ dur = 0.2, gain = 0.3, t, hp = 200, lp = 8000, q = 1, sweepLp = 0, dest }) {
    if (!this.ready) return;
    t = t ?? this.t;
    const s = this.ctx.createBufferSource();
    s.buffer = this.noiseBuf;
    const hpF = this.ctx.createBiquadFilter();
    hpF.type = 'highpass'; hpF.frequency.value = hp;
    const lpF = this.ctx.createBiquadFilter();
    lpF.type = 'lowpass'; lpF.frequency.setValueAtTime(lp, t); lpF.Q.value = q;
    if (sweepLp) lpF.frequency.exponentialRampToValueAtTime(Math.max(80, sweepLp), t + dur);
    const g = this.ctx.createGain();
    this._env(g, t, 0.004, dur, gain);
    s.connect(hpF).connect(lpF).connect(g).connect(dest || this.sfxBus);
    s.start(t);
    s.stop(t + dur + 0.06);
  }

  // --- 効果音 --------------------------------------------------------

  peg(power) {
    if (!this.ready) return;
    // 大量に鳴るので間引く
    const now = this.t;
    if (now - this._lastPeg < 0.012) return;
    this._lastPeg = now;
    const p = clamp(power / 8, 0.12, 1);
    const f = 900 + Math.random() * 1400 + p * 700;
    this.tone({ freq: f, type: 'triangle', dur: 0.05, attack: 0.001, gain: 0.055 + p * 0.10 });
    this.noise({ dur: 0.035, gain: 0.03 + p * 0.05, hp: 2500, lp: 11000 });
  }

  // 玉同士がぶつかる音。釘とは違い、鋼球同士の高く硬いカチッという音にする
  ballClack(power) {
    if (!this.ready) return;
    const now = this.t;
    if (now - this._lastClack < 0.02) return;
    this._lastClack = now;
    const p = clamp(power / 10, 0.1, 1);
    const f = 2400 + Math.random() * 900;
    this.tone({ freq: f, type: 'sine', dur: 0.035, attack: 0.001, gain: 0.05 + p * 0.07 });
    this.tone({ freq: f * 1.48, type: 'sine', dur: 0.025, attack: 0.001, gain: 0.03 + p * 0.04 });
    this.noise({ dur: 0.022, gain: 0.03 + p * 0.04, hp: 4000, lp: 14000 });
  }

  // ホールを歩く音
  step() {
    if (!this.ready) return;
    this.noise({ dur: 0.05, gain: 0.05, hp: 300, lp: 1400, sweepLp: 600 });
  }

  // 台を調べる / 決定
  select() {
    const t = this.t;
    this.tone({ freq: 880, type: 'square', dur: 0.07, gain: 0.10, t });
    this.tone({ freq: 1320, type: 'square', dur: 0.09, gain: 0.08, t: t + 0.05 });
  }

  windmill() {
    this.tone({ freq: 320 + Math.random() * 160, type: 'square', dur: 0.09, gain: 0.07, sweepTo: 200 });
  }

  launch(power) {
    this.noise({ dur: 0.16, gain: 0.14, hp: 400, lp: 1200, sweepLp: 5000 + power * 4000 });
    this.tone({ freq: 180 + power * 120, type: 'sawtooth', dur: 0.14, gain: 0.09, sweepTo: 520 + power * 400 });
  }

  drain() {
    this.tone({ freq: 150, type: 'sine', dur: 0.16, gain: 0.09, sweepTo: 70 });
  }

  railReturn() {
    this.tone({ freq: 260, type: 'triangle', dur: 0.2, gain: 0.1, sweepTo: 120 });
  }

  pocketSide() {
    const t = this.t;
    this.tone({ freq: 880, type: 'sine', dur: 0.16, gain: 0.16, t });
    this.tone({ freq: 1320, type: 'sine', dur: 0.14, gain: 0.10, t: t + 0.04 });
  }

  pocketStart() {
    const t = this.t;
    [1046, 1568, 2093].forEach((f, i) => {
      this.tone({ freq: f, type: 'triangle', dur: 0.22, gain: 0.2, t: t + i * 0.045 });
    });
    this.noise({ dur: 0.1, gain: 0.09, hp: 3000, lp: 12000, t });
  }

  attackerHit() {
    const t = this.t;
    this.tone({ freq: 120, type: 'sine', dur: 0.22, gain: 0.34, sweepTo: 60, t });
    this.tone({ freq: 1200, type: 'square', dur: 0.1, gain: 0.14, sweepTo: 2400, t });
    this.noise({ dur: 0.14, gain: 0.16, hp: 1500, lp: 9000, t });
  }

  payout() {
    const t = this.t + Math.random() * 0.02;
    this.tone({ freq: 1800 + Math.random() * 900, type: 'square', dur: 0.06, gain: 0.09, t });
  }

  gate(open) {
    const t = this.t;
    this.noise({ dur: 0.22, gain: 0.24, hp: 300, lp: open ? 6000 : 2200, sweepLp: open ? 1200 : 400, t });
    this.tone({ freq: open ? 220 : 160, type: 'square', dur: 0.16, gain: 0.16, sweepTo: open ? 420 : 90, t });
  }

  reelSpin() {
    this.noise({ dur: 0.3, gain: 0.1, hp: 600, lp: 3000, sweepLp: 7000 });
  }

  reelTick() {
    this.tone({ freq: 2600 + Math.random() * 400, type: 'square', dur: 0.022, gain: 0.05 });
  }

  reelStop(index) {
    const t = this.t;
    this.tone({ freq: 340 - index * 40, type: 'square', dur: 0.14, gain: 0.24, sweepTo: 130, t });
    this.noise({ dur: 0.1, gain: 0.14, hp: 800, lp: 6000, t });
  }

  reach() {
    const t = this.t;
    // 上昇するテンションの効果音
    for (let i = 0; i < 5; i++) {
      this.tone({ freq: 300 * Math.pow(1.26, i), type: 'sawtooth', dur: 0.34, gain: 0.12, t: t + i * 0.1, sweepTo: 300 * Math.pow(1.26, i + 1) });
    }
    this.noise({ dur: 1.0, gain: 0.1, hp: 200, lp: 800, sweepLp: 7000, t });
  }

  superReach() {
    const t = this.t;
    this.tone({ freq: 80, type: 'sawtooth', dur: 1.4, gain: 0.24, sweepTo: 240, t });
    this.noise({ dur: 1.4, gain: 0.16, hp: 100, lp: 500, sweepLp: 9000, t });
  }

  jackpot() {
    const t = this.t;
    const chord = [523, 659, 784, 1046, 1319, 1568, 2093];
    chord.forEach((f, i) => {
      this.tone({ freq: f, type: 'sawtooth', dur: 1.1, gain: 0.16, t: t + i * 0.06 });
      this.tone({ freq: f * 2, type: 'triangle', dur: 0.8, gain: 0.08, t: t + i * 0.06 });
    });
    this.tone({ freq: 65, type: 'sine', dur: 1.6, gain: 0.4, t });
    this.noise({ dur: 1.2, gain: 0.2, hp: 2000, lp: 14000, t });
  }

  roundStart(n) {
    const t = this.t;
    [880, 1108, 1318].forEach((f, i) =>
      this.tone({ freq: f * (1 + n * 0.012), type: 'square', dur: 0.18, gain: 0.16, t: t + i * 0.06 }));
  }

  lose() {
    const t = this.t;
    [440, 415, 392].forEach((f, i) =>
      this.tone({ freq: f, type: 'triangle', dur: 0.26, gain: 0.13, t: t + i * 0.12 }));
  }

  empty() {
    const t = this.t;
    [330, 262].forEach((f, i) =>
      this.tone({ freq: f, type: 'sawtooth', dur: 0.4, gain: 0.14, t: t + i * 0.18, sweepTo: f * 0.6 }));
  }

  // --- BGM -----------------------------------------------------------

  setMusic(mode) {
    if (this._musicMode === mode) return;
    this._musicMode = mode;
    this._step = 0;
  }

  _startScheduler() {
    const lookahead = 0.12;
    const tick = () => {
      if (!this.ready) return;
      while (this._nextNoteTime < this.t + lookahead) {
        this._playStep(this._step, this._nextNoteTime);
        const spb = 60 / this._tempo();
        this._nextNoteTime += spb / 4;   // 16分音符
        this._step = (this._step + 1) % 32;
      }
    };
    this._timer = setInterval(tick, 25);
  }

  _tempo() {
    return this._musicMode === 'jackpot' ? 148 : this._musicMode === 'reach' ? 132 : 104;
  }

  _playStep(s, t) {
    const m = this._musicMode;
    if (m === 'idle' || this.muted) return;
    const bus = this.musicBus;

    if (m === 'normal') {
      const scale = [220, 261.6, 293.7, 329.6, 392, 440, 523.3];
      if (s % 2 === 0) {
        const n = scale[(s / 2) % scale.length];
        this.tone({ freq: n * 2, type: 'triangle', dur: 0.26, gain: 0.10, t, dest: bus });
      }
      if (s % 8 === 0) this.tone({ freq: 110, type: 'sine', dur: 0.4, gain: 0.24, t, dest: bus });
      if (s % 4 === 2) this.noise({ dur: 0.04, gain: 0.05, hp: 6000, lp: 14000, t, dest: bus });
    } else if (m === 'reach') {
      const bass = [110, 116.5, 123.5, 130.8];
      if (s % 4 === 0) {
        this.tone({ freq: bass[(s / 4) % 4], type: 'sawtooth', dur: 0.3, gain: 0.2, t, dest: bus });
        this.tone({ freq: 55, type: 'sine', dur: 0.28, gain: 0.3, t, dest: bus });
      }
      if (s % 2 === 1) this.noise({ dur: 0.05, gain: 0.07, hp: 5000, lp: 15000, t, dest: bus });
      if (s % 8 === 4) this.tone({ freq: 1200, type: 'square', dur: 0.1, gain: 0.08, t, dest: bus, sweepTo: 1800 });
    } else if (m === 'jackpot') {
      const arp = [523.3, 659.3, 784, 1046.5, 784, 659.3];
      this.tone({ freq: arp[s % arp.length], type: 'square', dur: 0.13, gain: 0.10, t, dest: bus });
      if (s % 4 === 0) {
        this.tone({ freq: 65, type: 'sine', dur: 0.22, gain: 0.42, t, dest: bus, sweepTo: 45 });
        this.noise({ dur: 0.05, gain: 0.1, hp: 40, lp: 200, t, dest: bus });
      }
      if (s % 8 === 4) this.noise({ dur: 0.14, gain: 0.14, hp: 1200, lp: 9000, t, dest: bus });
      if (s % 2 === 1) this.noise({ dur: 0.03, gain: 0.05, hp: 7000, lp: 15000, t, dest: bus });
    }
  }
}
